/**
 * Shared forgot-password form — body + success state extracted
 * from ForgotPasswordPage so AuthPromptModal can reuse it.
 *
 * Always shows the success state regardless of whether the email
 * actually exists (defense against enumeration). The user follows
 * the reset link from their inbox; `/reset-password` is its own
 * standalone page, not part of this modal.
 */
import { useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Mail } from 'lucide-react'
import { authClient } from '@papan/auth/client'
import { useTranslation } from '@papan/i18n'
import { safeNext } from '../lib/safe-next'
import { refusalIsSafeToSurface } from '../lib/refusal'

export interface ForgotPasswordFormProps {
  /**
   * Prefill for the email field. Passed by the Settings entry point, where the
   * user is signed in and the app already knows the address — asking a
   * signed-in person to retype it reads as if we do not know who they are.
   * Anonymous surfaces (/forgot-password, AuthPromptModal) pass nothing.
   */
  defaultEmail?: string
}

export function ForgotPasswordForm({ defaultEmail = '' }: ForgotPasswordFormProps = {}) {
  const { t } = useTranslation('auth')
  const [searchParams] = useSearchParams()
  // Initial value only, which is right: every surface that passes one mounts
  // this form fresh when it opens, so there is no stale-prop case to sync — and
  // a sync effect would fight a user who has deliberately typed a different
  // address (resetting the password of a secondary account, say).
  const [email, setEmail] = useState(defaultEmail)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [sent, setSent] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)
    try {
      // Preserve ?next= through the reset round-trip so a recipient who
      // detours via password-reset lands back at the share, not drive root.
      // Better-auth carries redirectTo as `callbackURL` (token lives in the
      // reset-link path); on the post-validation redirect it does
      // searchParams.set('token', …) on the decoded callbackURL — clean, no
      // double-`?` — so the SPA receives ?next=…&token=… and ResetPasswordPage
      // forwards next into the post-reset /login.
      // Sanitize with safeNext here too (defense-in-depth + parity with the
      // other auth pages) so an unsanitized value never rides into the email
      // link, even if a future code path navigates to it without re-checking.
      const rawNext = searchParams.get('next')
      const resetBase = `${window.location.origin}/reset-password`
      const redirectTo = rawNext
        ? `${resetBase}?next=${encodeURIComponent(safeNext(rawNext))}`
        : resetBase
      const result = await authClient.forgetPassword.email({
        email,
        redirectTo,
      })
      // `authFetch` RETURNS `{ error }` rather than throwing, so the catch
      // below is dead for HTTP errors and this result used to be discarded —
      // every outcome rendered "check your email", which is #610's signature.
      //
      // WHICH refusals may be shown, and why a 5xx may not, lives in
      // `refusalIsSafeToSurface` — one implementation, because the resend button
      // on VerifyEmailPage needs the same rule and a second copy already drifted
      // once. Behaviour is pinned by the matrix in
      // `forgot-password-refusal.test.tsx`, not by a source grep.
      const status = result?.error?.status
      if (result?.error && refusalIsSafeToSurface(status)) {
        setError(status === 429 ? t('common:tooManyRequests') : t('common:requestFailed'))
        return
      }
      setSent(true)
    } catch {
      // Always show success — enumeration defense.
      setSent(true)
    } finally {
      setIsLoading(false)
    }
  }

  if (sent) {
    return (
      <div className="text-center py-4">
        <Mail size={40} className="mx-auto mb-4 themed-text-muted" />
        <h2 className="text-lg font-semibold themed-text mb-2">
          {t('forgotPassword.successTitle')}
        </h2>
        <p className="text-sm themed-text-secondary">
          {t('forgotPassword.successDescription', { email })}
        </p>
      </div>
    )
  }

  return (
    <>
      <p className="text-sm themed-text-secondary mb-4">
        {t('forgotPassword.description')}
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* role="alert" rather than "status": this is a refusal the user must
            act on, and the submit button's label flips straight back from
            "Sending…" to "Send reset link" — so without an assertive live
            region a screen-reader user gets exactly the silent failure this
            whole change exists to remove. */}
        {error && (
          <div role="alert" className="p-3 text-sm text-red-400 bg-red-900/20 border border-red-800 rounded">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="forgot-email" className="block text-sm themed-text-secondary mb-1">
            {t('forgotPassword.email')}
          </label>
          <input
            id="forgot-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full themed-input border rounded-md px-3 py-2 outline-none focus:border-blue-500"
            required
            autoFocus
          />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm transition-colors"
        >
          {isLoading ? t('forgotPassword.submitting') : t('forgotPassword.submit')}
        </button>
      </form>
    </>
  )
}
